import React, { Component } from 'react';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { useTheme } from '@mui/material/styles';
import { Box, Button, Divider, Drawer, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import {
  Add as AddIcon,
  AddCircle as AddBoldIcon,
  LocalGroceryStore as LocalGroceryStoreIcon,
  StoreMallDirectory as StoreMallDirectoryIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  AccountCircle as AccountCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  SupervisedUserCircle as UsersIcon,
  Map as MapIcon,
  PhotoLibrary as PhotoLibraryIcon,
  EmojiPeople as EmojiPeopleIcon,
  Email as EmailIcon,
  ThumbUp as ThumbUpIcon,
  School as SchoolIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';
import { Roles } from '../models/roles';
import * as authService from '../services/auth';
import routes from '../routes';
import IconWithText from './IconWithText';
import errorMessages from '../assets/errorMessages';

const drawerWidth = 220;
const drawerPaperSx = { width: drawerWidth };
const drawerPaperCloseSx = (theme) => ({
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: theme.spacing(7),
});

const ListItemLink = ({ icon, primary, to, isDrawerOpen, dataTestId, onLinkClick }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isActive = pathname.includes(to);

  if (isDrawerOpen) {
    return (
      <li>
        <ListItem button component={Link} to={to} onClick={onLinkClick} data-testid={dataTestId}>
          <ListItemIcon style={isActive ? { color: theme.palette.primary.main } : {}}>{icon}</ListItemIcon>
          <ListItemText primary={primary} />
        </ListItem>
      </li>
    );
  }

  return (
    <li>
      <Tooltip title={primary} placement="right" arrow>
        <ListItem button component={Link} to={to} onClick={onLinkClick} data-testid={dataTestId}>
          <ListItemIcon style={isActive ? { color: theme.palette.primary.main } : {}}>{icon}</ListItemIcon>
        </ListItem>
      </Tooltip>
    </li>
  );
};

class MainDrawer extends Component {
  renderDrawerContent = () => {
    const { closeDrawer, user, isDrawerOpen, isMobileView, featureFlagsList, disableRequestToDonate } = this.props;

    const currentlyLoggedInUser = authService.getCurrentlyLoggedInUser();
    let showSitesInMenu = false;

    if (user.role_assignments !== undefined) {
      showSitesInMenu = authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
      ])
        ? true
        : authService.getCurrentlyLoggedInOrImpersonatingUserAvailableSitesIds().length > 1;
    }

    const handleListItemClick = () => {
      if (isMobileView) {
        closeDrawer();
      }
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 8px',
            paddingTop: '4px',
            paddingBottom: '3px',
          }}
        >
          <IconButton onClick={closeDrawer} size="large">
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        <List
          sx={{
            '&:empty': {
              padding: 0,
            },
          }}
        >
          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.Admin,
            Roles.NationalSiteDirector,
            Roles.SiteDirector,
            Roles.SiteCoordinator,
          ]) && (
            <>
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.rescuesList)}
                primary="Rescues"
                dataTestId="donations"
                icon={<LocalGroceryStoreIcon data-introid="drawer-donations" />}
              />
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.allUsers)}
                primary="Users"
                icon={<UsersIcon data-introid="drawer-users" />}
                dataTestId="users-menu"
              />
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={routes.map}
                primary="Map"
                icon={<MapIcon data-introid="map" />}
                dataTestId="map"
              />

              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={routes.rescuesGallery}
                primary="Rescue Photo Gallery"
                icon={<PhotoLibraryIcon data-introid="rescue-gallery" />}
                dataTestId="rescue-photo-gallery"
              />
            </>
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.Admin,
            Roles.NationalSiteDirector,
            Roles.SiteDirector,
            Roles.SiteCoordinator,
            Roles.DonorAdmin,
            Roles.DonorStaff,
            // Remove feature flag when ready
            get(featureFlagsList, 'receivingAgencyReport', false) && Roles.ReceiverLocationAdmin,
          ]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.reports)}
              primary="Reports"
              icon={<IconWithText text="REP" icon={<TrendingUpIcon data-introid="drawer-reports" />} />}
            />
          )}

          {showSitesInMenu && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={routes.sites}
              primary="Sites"
              icon={<StoreMallDirectoryIcon data-introid="drawer-sites-menu" />}
              dataTestId="sites-menu"
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.Admin,
            Roles.NationalSiteDirector,
            Roles.SiteDirector,
            Roles.SiteCoordinator,
          ]) && (
            <>
              {get(featureFlagsList, 'marketingHubFF', false) && (
                <ListItemLink
                  onLinkClick={handleListItemClick}
                  isDrawerOpen={isDrawerOpen}
                  to={generatePath(routes.marketingHub)}
                  primary="Marketing Hub"
                  icon={<ThumbUpIcon data-introid="drawer-marketing-hub" />}
                />
              )}

              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.communications)}
                primary="Communications"
                icon={<EmailIcon data-introid="drawer-communications" />}
              />

              {get(featureFlagsList, 'events', false) && (
                <ListItemLink
                  onLinkClick={handleListItemClick}
                  isDrawerOpen={isDrawerOpen}
                  to={routes.eventsList}
                  primary="Events"
                  icon={<EmojiPeopleIcon data-introid="drawer-events" />}
                />
              )}
            </>
          )}
        </List>
        <Divider />
        <List>
          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([Roles.Rescuer]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.dashboardRescuer)}
              primary="Dashboard Rescuer"
              dataTestId="dashboard-rescuer"
              icon={<DashboardIcon />}
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.SiteCoordinator,
            Roles.SiteDirector,
          ]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.dashboardSC)}
              primary="Dashboard SD/SC"
              icon={<DashboardIcon />}
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.DonorLocationAdmin,
            Roles.DonorLocationStaff,
          ]) && (
            <>
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.dashboardFoodDonor)}
                primary="Food Donor Dashboard"
                dataTestId="drawer-food-donor-dashboard"
                icon={<DashboardIcon />}
              />

              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.donation_requests)}
                primary="Donation Requests"
                icon={<LocalGroceryStoreIcon data-introid="drawer-donations" />}
                dataTestId="donation-requests"
              />

              <li>
                <Tooltip
                  title={(() => {
                    if (disableRequestToDonate) {
                      return errorMessages.FOOD_DONOR_ACCOUNT_NOT_VERIFIED.message;
                    }
                    return isDrawerOpen ? '' : 'Request to Donate';
                  })()}
                  placement="right"
                >
                  <span>
                    <ListItem
                      disabled={disableRequestToDonate}
                      button
                      component={Link}
                      to={routes.donation_create_request}
                      data-testid="request-to-donate"
                    >
                      {isDrawerOpen ? (
                        <Button startIcon={<AddIcon />} type="button" variant="contained" color="primary" size="small">
                          Request to Donate
                        </Button>
                      ) : (
                        <ListItemIcon>
                          <AddBoldIcon color="secondary" />
                        </ListItemIcon>
                      )}
                    </ListItem>
                  </span>
                </Tooltip>
              </li>
            </>
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.ReceiverAdmin,
            Roles.ReceiverStaff,
            Roles.ReceiverLocationAdmin,
            Roles.ReceiverLocationStaff,
          ]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.dashboardReceiver)}
              primary="Dashboard Receiving Agency"
              icon={<DashboardIcon />}
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
            Roles.NationalSiteDirector,
          ]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.dashboardNSD)}
              primary="Dashboard NSD"
              icon={<DashboardIcon />}
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([Roles.Admin]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.dashboardAdmin)}
              primary="Admin Dashboard"
              icon={<DashboardIcon />}
            />
          )}

          {get(featureFlagsList, 'learningCenterFF', false) &&
            authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
              Roles.Admin,
              Roles.NationalSiteDirector,
              Roles.SiteCoordinator,
              Roles.SiteDirector,
              Roles.Rescuer,
            ]) && (
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={generatePath(routes.learn)}
                primary="Learn"
                icon={<SchoolIcon />}
              />
            )}

          <ListItemLink
            onLinkClick={handleListItemClick}
            isDrawerOpen={isDrawerOpen}
            to={routes.contactUs}
            primary="Contact Us"
            icon={<ContactSupportIcon />}
            dataTestId="contact-us-menu"
          />

          {get(featureFlagsList, 'taxDocumentsFF', false) &&
            authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
              Roles.DonorLocationAdmin,
              Roles.DonorLocationStaff,
              Roles.DonorAdmin,
              Roles.DonorStaff,
            ]) && (
              <ListItemLink
                onLinkClick={handleListItemClick}
                isDrawerOpen={isDrawerOpen}
                to={routes.taxDocuments}
                primary="Tax Documents"
                icon={<DescriptionIcon data-introid="drawer-tax-documents" />}
                dataTestId="tax-documents"
              />
            )}

          {authService.hasAnyRoleInCurrentlySelectedSite(currentlyLoggedInUser, [
            Roles.Admin,
            Roles.NationalSiteDirector,
          ]) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={routes.switchUser}
              primary="Switch User"
              icon={<AccountCircleIcon />}
              dataTestId="switch-user-menu"
            />
          )}

          {authService.currentlyLoggedInOrImpersonatingUserHasRoleInCurrentlySelectedSite(Roles.Admin) && (
            <ListItemLink
              onLinkClick={handleListItemClick}
              isDrawerOpen={isDrawerOpen}
              to={generatePath(routes.settings)}
              primary="Manage Settings"
              icon={<SettingsIcon />}
            />
          )}
        </List>
      </>
    );
  };

  render() {
    const { isDrawerOpen, isMobileView } = this.props;

    return (
      <div data-introid="main-drawer">
        {isMobileView ? (
          <Drawer
            variant="temporary"
            sx={(theme) => ({ '& .MuiDrawer-paper': [drawerPaperSx, !isDrawerOpen && drawerPaperCloseSx(theme)] })}
            open={isDrawerOpen}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {this.renderDrawerContent()}
          </Drawer>
        ) : (
          <Drawer
            sx={(theme) => ({ '& .MuiDrawer-paper': [drawerPaperSx, !isDrawerOpen && drawerPaperCloseSx(theme)] })}
            variant="permanent"
            open={isDrawerOpen}
          >
            {this.renderDrawerContent()}
          </Drawer>
        )}
      </div>
    );
  }
}

export default MainDrawer;
